import { Injectable } from '@angular/core';

interface ChallengeLayerStyles {
  transform: string;
  transformOrigin: string;
}

/**
 * Ajusta una sola vez por documento el desafío visual de Google reCAPTCHA.
 *
 * Google inserta el iframe del desafío fuera del componente que lo abrió. Por
 * eso este comportamiento vive en un servicio singleton y no en cada átomo.
 */
@Injectable({ providedIn: 'root' })
export class UiRecaptchaChallengeFitService {
  private consumers = 0;
  private observer: MutationObserver | null = null;
  private layer: HTMLElement | null = null;
  private originalStyles: ChallengeLayerStyles | null = null;
  private readonly onViewportChange = (): void => this.fit();

  connect(): void {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }

    this.consumers += 1;
    if (this.consumers > 1) return;

    this.observer = new MutationObserver(() => this.fit());
    this.observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', this.onViewportChange);
    this.fit();
  }

  disconnect(): void {
    if (this.consumers === 0) return;
    this.consumers -= 1;
    if (this.consumers > 0) return;

    this.observer?.disconnect();
    this.observer = null;
    window.removeEventListener('resize', this.onViewportChange);
    this.restoreLayer();
  }

  private fit(): void {
    const iframe = document.querySelector<HTMLIFrameElement>(
      'iframe[src*="/recaptcha/api2/bframe"], iframe[src*="/recaptcha/enterprise/bframe"]',
    );
    if (!iframe) {
      this.restoreLayer();
      return;
    }

    const layer = this.findChallengePanel(iframe);
    if (this.layer !== layer) {
      this.restoreLayer();
      this.layer = layer;
      this.originalStyles = {
        transform: layer.style.transform,
        transformOrigin: layer.style.transformOrigin,
      };
    }

    this.restoreTransform();
    const rect = layer.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;

    const horizontalRoom = Math.max(120, window.innerWidth - 16);
    const verticalRoom = Math.max(120, window.innerHeight - 16);
    const scale = Math.min(
      1,
      horizontalRoom / rect.width,
      verticalRoom / rect.height,
    );
    if (scale >= 0.999) return;

    layer.style.transformOrigin = 'center center';
    layer.style.transform = `scale(${scale})`;
  }

  /**
   * El panel es el ancestro posicionado más cercano que conserva las
   * dimensiones del iframe, no la capa exterior que cubre todo el viewport.
   */
  private findChallengePanel(iframe: HTMLIFrameElement): HTMLElement {
    const iframeRect = iframe.getBoundingClientRect();
    let current = iframe.parentElement;

    while (current && current !== document.body) {
      const rect = current.getBoundingClientRect();
      const positioned = ['absolute', 'fixed'].includes(
        getComputedStyle(current).position,
      );
      const containsIframe =
        rect.width >= iframeRect.width && rect.height >= iframeRect.height;
      const isViewportLayer =
        rect.width >= window.innerWidth * 0.95 &&
        rect.height >= window.innerHeight * 0.95;

      if (positioned && containsIframe && !isViewportLayer) return current;
      current = current.parentElement;
    }

    return iframe;
  }

  private restoreTransform(): void {
    if (!this.layer || !this.originalStyles) return;
    this.layer.style.transform = this.originalStyles.transform;
    this.layer.style.transformOrigin = this.originalStyles.transformOrigin;
  }

  private restoreLayer(): void {
    this.restoreTransform();
    this.layer = null;
    this.originalStyles = null;
  }
}
